// src/pages/MensagensInternas.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
} from "react";

import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import { useNotify } from "../context/notifyStore";
import { resolveCurrentUserId } from "../utils/currentUserId";

import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import SelectFuncionario from "../components/ui/selectFuncionario";
import "../sass/pages/mensagensInternas.scss";

// 🔌 WebSocket (Socket.IO)
import { useSocket } from "../ws/SocketProvider";

/* =============== Helpers =============== */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const STORAGE_USER_KEY = "SSW_MSG_USER";
const STORAGE_LAST_THREAD = "SSW_MSG_LAST_THREAD";
const STORAGE_FILTER_TIPO = "SSW_MSG_FILTER_TIPO";
const STORAGE_SEARCH = "SSW_MSG_SEARCH";

// → garante inteiro seguro (BigInt no back não aceita decimal)
const toInt = (v) => {
  if (typeof v === "bigint") return Number(v);
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : null;
};

function logAxiosError(err, ctx = "request") {
  const isAxios = !!(err && err.config);
  const url = isAxios ? err.config?.url : undefined;
  const method = isAxios ? err.config?.method : undefined;
  const status = err?.response?.status;
  const data = err?.response?.data;
  const message = err?.message;
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `[NET][${ctx}] ${method?.toUpperCase?.() || "-"} ${url} :: ${
      status || message
    }`
  );
  // eslint-disable-next-line no-console
  console.log({ url, method, status, data, message, err });
  // eslint-disable-next-line no-console
  console.groupEnd();
}

const getFuncId = (f) => toInt(f?.IdFuncionario ?? f?.id ?? f?.ID) || null;

const addParticipante = async (f, userId, setFormCreate, mergeNames) => {
  const id = getFuncId(f);
  if (!id) return;
  setFormCreate((s) => {
    if (s.tipo === "DM" && s.participantes.length >= 1) return s;
    const next = Array.from(new Set([...s.participantes, id])).filter(Boolean);
    const sanitized = next.filter((x) => x !== toInt(userId));
    return { ...s, participantes: sanitized };
  });
  try {
    const map = await fetchNamesBatch([id]);
    mergeNames(map);
  } catch {}
};

const removeParticipante = (id, setFormCreate) =>
  setFormCreate((s) => ({
    ...s,
    participantes: s.participantes.filter((x) => x !== id),
  }));

/* =============== API (x-user-id) =============== */
const withUid = (uid) => ({ headers: { "x-user-id": uid } });

const MsgApi = {
  createThread: async (payload, uid) => {
    try {
      const r = await api.post(`/ssw/msg/threads`, payload, withUid(uid));
      return r.data;
    } catch (e) {
      logAxiosError(e, "createThread");
      throw e;
    }
  },
  listThreads: async ({ search = "", limit = 30, afterId = null }, uid) => {
    const params = { limit };
    const ai = toInt(afterId);
    if (search) params.search = search;
    if (ai && ai > 0) params.afterId = ai; // só manda inteiro > 0
    try {
      const r = await api.get(`/ssw/msg/threads`, { params, ...withUid(uid) });
      return r.data;
    } catch (e) {
      logAxiosError(e, "listThreads");
      throw e;
    }
  },
  listMessages: async ({ threadId, afterId = null, limit = 50 }, uid) => {
    const params = { limit };
    const ai = toInt(afterId);
    if (ai && ai > 0) params.afterId = ai; // só manda inteiro > 0
    try {
      const r = await api.get(`/ssw/msg/threads/${threadId}/messages`, {
        params,
        ...withUid(uid),
      });
      return r.data;
    } catch (e) {
      logAxiosError(e, "listMessages");
      throw e;
    }
  },
  postMessage: async (
    { threadId, body, reply_to_id = null, mentions = [] },
    uid
  ) => {
    const payload = { body };
    const rid = toInt(reply_to_id);
    if (rid) payload.reply_to_id = rid;
    if (mentions?.length) payload.mentions = mentions;
    try {
      const r = await api.post(
        `/ssw/msg/threads/${threadId}/messages`,
        payload,
        withUid(uid)
      );
      return r.data; // { id }
    } catch (e) {
      logAxiosError(e, "postMessage");
      throw e;
    }
  },
  setReadPointer: async ({ threadId, last_read_message_id }, uid) => {
    const lid = toInt(last_read_message_id);
    if (!lid) return;
    try {
      return await api.put(
        `/ssw/msg/threads/${threadId}/read-pointer`,
        { last_read_message_id: lid },
        withUid(uid)
      );
    } catch (e) {
      logAxiosError(e, "setReadPointer");
      throw e;
    }
  },
};

/* =============== Names (id → nome) =============== */
const pickName = (obj) =>
  obj?.NOME ??
  obj?.NOME_DEV ??
  obj?.NOME_FUNCIONARIO ??
  obj?.NOME_FUN ??
  obj?.NOME_COMPLETO ??
  obj?.NOME_USUARIO ??
  obj?.USUARIO_NOME ??
  obj?.AUTOR_NOME ??
  obj?.Nome ??
  obj?.nome ??
  null;

const normalizeBatchNames = (payload) => {
  if (!payload) return {};
  const out = {};
  if (payload.map && typeof payload.map === "object") {
    for (const [k, v] of Object.entries(payload.map)) {
      const id = toInt(k);
      const name = typeof v === "string" ? v : pickName(v);
      if (id && name) out[id] = String(name);
    }
    return out;
  }
  if (Array.isArray(payload)) {
    payload.forEach((x) => {
      const id = toInt(x?.IdFuncionario ?? x?.ID ?? x?.id);
      const name = pickName(x);
      if (id && name) out[id] = String(name);
    });
    return out;
  }
  if (typeof payload === "object") {
    for (const k of Object.keys(payload)) {
      const id = toInt(k);
      const v = payload[k];
      const name = typeof v === "string" ? v : pickName(v);
      if (id && name) out[id] = String(name);
    }
  }
  return out;
};

async function fetchNamesBatch(ids) {
  const uniq = Array.from(new Set(ids.map(toInt).filter(Boolean)));
  if (!uniq.length) return {};
  try {
    const { data } = await api.get("/func/names", {
      params: { ids: uniq.join(",") },
    });
    return normalizeBatchNames(data);
  } catch (e) {
    logAxiosError(e, "func/names");
    const m = {};
    await Promise.all(
      uniq.map(async (id) => {
        try {
          const { data } = await api.get(`/func/${id}`);
          const name = pickName(data);
          if (name) m[id] = String(name);
        } catch (e2) {
          logAxiosError(e2, "func/{id}");
        }
      })
    );
    return m;
  }
}

/* ===== Normalização de mensagens/ids ===== */
const normalizeMsg = (m) => ({
  ...m,
  autor_id: toInt(m.autor_id),
  id: toInt(m.id),
});
const isRealId = (v) => Number.isInteger(v) && v > 0;
const maxRealMsgId = (list) =>
  (list || []).reduce((mx, m) => (isRealId(m.id) ? Math.max(mx, m.id) : mx), 0) ||
  null;

/* =============== Drawer =============== */
function PanelDrawer({ open, title, onClose, children }) {
  const firstFieldRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="drawer" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="drawer-body" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h2>{title}</h2>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>
        {typeof children === "function" ? children({ firstFieldRef }) : children}
      </div>
    </div>
  );
}

/* =============== Página =============== */
export default function MensagensInternas() {
  const { setBadge, clearBadge } = useNotify();
  const { socket } = useSocket();

  // usuário ativo
  const [userId] = useState(() => resolveCurrentUserId());

  // cache de nomes
  const [peopleNames, setPeopleNames] = useState({});
  const getName = (id) =>
    id && peopleNames[id] ? peopleNames[id] : id ? `#${id}` : "-";
  const mergeNames = (obj) =>
    setPeopleNames((prev) => ({
      ...prev,
      ...obj,
    }));

  // bootstrap usuário + nome
  useEffect(() => {
    if (!userId) {
      showError("Usuário não identificado. Refaça o login.");
      return;
    }

    localStorage.setItem(STORAGE_USER_KEY, String(userId));
    (async () => {
      try {
        const map = await fetchNamesBatch([userId]);
        mergeNames(map);
      } catch {
        // silencioso
      }
    })();
  }, [userId]);

  // ao entrar, zera badge de mensagens
  useEffect(() => {
    clearBadge?.("mensagens");
  }, [clearBadge]);

  // filtros/sidebar
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [search, setSearch] = useState(
    localStorage.getItem(STORAGE_SEARCH) || ""
  );
  const deferredSearch = useDeferredValue(search);
  const [filterTipo, setFilterTipo] = useState(
    localStorage.getItem(STORAGE_FILTER_TIPO) || "ALL"
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_FILTER_TIPO, filterTipo);
  }, [filterTipo]);
  useEffect(() => {
    localStorage.setItem(STORAGE_SEARCH, search);
  }, [search]);

  // painel
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  // composer
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);

  // drawer: nova conversa
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formCreate, setFormCreate] = useState({
    tipo: "DM",
    titulo: "",
    participantes: [],
  });

  // garante nomes dos chips do modal
  useEffect(() => {
    (async () => {
      const missing = (formCreate.participantes || []).filter(
        (id) => !peopleNames[id]
      );
      if (missing.length) mergeNames(await fetchNamesBatch(missing));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCreate.participantes]);

  // infra
  const msgEndRef = useRef(null);
  const panelRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const selectedRef = useRef(null);
  const didAutoSelectRef = useRef(false);

  // near-bottom detector
  const isNearBottom = useCallback(() => {
    const el = panelRef.current;
    if (!el) return true;
    const threshold = 120;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  const scrollToEnd = useCallback(async () => {
    await sleep(10);
    msgEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, []);

  // normaliza participantes
  const normalizeParticipants = (t) => {
    const p =
      t.participantes ??
      t.participants ??
      t.members ??
      t.membros ??
      t.participantes_ids ??
      t.participants_ids ??
      t.members_ids ??
      t.membros_ids ??
      t.participantes_json ??
      t.participants_json ??
      null;
    try {
      if (Array.isArray(p)) return p.map(toInt).filter(Boolean);
      if (p == null) return [];
      if (typeof p === "string") {
        const s = p.trim();
        if (s.startsWith("[") && s.endsWith("]")) {
          return JSON.parse(s).map(toInt).filter(Boolean);
        }
        return s
          .split(",")
          .map((x) => toInt(String(x).trim()))
          .filter(Boolean);
      }
      if (typeof p === "object" && Array.isArray(p.ids)) {
        return p.ids.map(toInt).filter(Boolean);
      }
    } catch {}
    return [];
  };

  // threads
  const loadThreads = useCallback(async () => {
    if (!userId) return;

    setThreadsLoading(true);
    try {
      const data = await MsgApi.listThreads(
        { search: deferredSearch, limit: 50 },
        userId
      );
      const hydrated = (Array.isArray(data) ? data : []).map((t) => ({
        ...t,
        id: toInt(t.id),
        last_msg_id: toInt(t.last_msg_id) || 0,
        last_read_message_id: toInt(t.last_read_message_id) || 0,
        participantes: normalizeParticipants(t),
      }));
      const arr =
        filterTipo === "ALL"
          ? hydrated
          : hydrated.filter((t) => t.tipo === filterTipo);
      setThreads(arr);

      const ids = new Set();
      arr.forEach((t) =>
        (t.participantes || []).forEach((id) => ids.add(toInt(id)))
      );
      if (ids.size) mergeNames(await fetchNamesBatch(Array.from(ids)));
    } catch (e) {
      showError(
        e?.response?.data?.message ||
          e.message ||
          "Falha ao listar conversas."
      );
    } finally {
      setThreadsLoading(false);
    }
  }, [deferredSearch, filterTipo, userId]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (!socket) return;
    const rejoin = () => {
      const t = selectedRef.current;
      if (t?.id) socket.emit("thread:join", t.id);
    };
    socket.on("connect", rejoin);
    return () => socket.off("connect", rejoin);
  }, [socket]);

  // mensagens
  const loadMessages = useCallback(
    async (threadId, afterId = null) => {
      if (!threadId) return;
      setMsgLoading(true);
      try {
        const raw = await MsgApi.listMessages(
          { threadId, afterId, limit: 50 },
          userId
        );
        const list = (Array.isArray(raw) ? raw : []).map(normalizeMsg);

        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const merged = [
            ...prev,
            ...list.filter((m) => !ids.has(m.id)),
          ].sort((a, b) => a.id - b.id);
          lastMsgIdRef.current = maxRealMsgId(merged);
          return merged;
        });

        const idAuthors = Array.from(
          new Set(list.map((m) => toInt(m.autor_id)))
        );
        if (idAuthors.length) mergeNames(await fetchNamesBatch(idAuthors));

        if (isNearBottom()) await scrollToEnd();
      } catch (e) {
        const st = e?.response?.status;
        if (st === 403 || st === 404) {
          showError(
            st === 403
              ? "Você não tem acesso a esta conversa."
              : "Conversa não encontrada."
          );
          setSelected(null);
          selectedRef.current = null;
          return;
        }
        showError(
          e?.response?.data?.message ||
            e.message ||
            "Falha ao buscar mensagens."
        );
      } finally {
        setMsgLoading(false);
      }
    },
    [isNearBottom, scrollToEnd, userId]
  );

  const selectThread = useCallback(
    async (t) => {
      const prev = selectedRef.current;
      if (socket && prev?.id && prev.id !== t?.id) {
        socket.emit("thread:leave", prev.id);
      }
      setSelected(t);
      selectedRef.current = t;
      localStorage.setItem(STORAGE_LAST_THREAD, String(t?.id || ""));
      setMessages([]);
      lastMsgIdRef.current = null;

      if (socket && t?.id) socket.emit("thread:join", t.id);

      await loadMessages(t.id);

      setTimeout(() => {
        const el = document.querySelector(".mi__composer .textarea");
        el && el.focus && el.focus();
      }, 0);

      // zera não lidas (read-pointer no último id conhecido)
      const last = toInt(t.last_msg_id);
      if (last) {
        try {
          await MsgApi.setReadPointer(
            { threadId: t.id, last_read_message_id: last },
            userId
          );
          setThreads((prev) =>
            prev.map((row) =>
              row.id === t.id
                ? {
                    ...row,
                    last_read_message_id: Math.max(
                      row.last_read_message_id || 0,
                      last
                    ),
                    unread_count: 0,
                  }
                : row
            )
          );
        } catch {}
      }

      const map = await fetchNamesBatch(t.participantes || []);
      mergeNames(map);
    },
    [loadMessages, userId, socket, setThreads]
  );

  // polling adaptativo (fallback ao WS)
  useEffect(() => {
    if (!selected) return;
    const baseMs =
      document.visibilityState === "visible" ? 4000 : 15000;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const t = selectedRef.current;
      if (!t) return;
      try {
        const lastKnown = isRealId(toInt(lastMsgIdRef.current))
          ? toInt(lastMsgIdRef.current)
          : 0;
        await loadMessages(t.id, lastKnown);
      } catch {}
    }, baseMs);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [selected, loadMessages, userId]);

  // sync ao voltar foco/visibilidade
  useEffect(() => {
    const onVis = async () => {
      if (!selectedRef.current) return;
      try {
        const last = isRealId(toInt(lastMsgIdRef.current))
          ? toInt(lastMsgIdRef.current)
          : 0;
        await loadMessages(selectedRef.current.id, last);
        if (last) {
          await MsgApi.setReadPointer(
            {
              threadId: selectedRef.current.id,
              last_read_message_id: last,
            },
            userId
          );
        }
      } catch {}
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [loadMessages, userId]);

  // === WS: handlers (notificação global + timeline em tempo real)
  useEffect(() => {
    if (!socket) return;

    socket.on("connect", () => {
      console.log(
        "[WS] connected",
        socket.id,
        "query=",
        socket.io?.opts?.query
      );
    });
    socket.on("connect_error", (err) => {
      console.error("[WS] connect_error:", err?.message || err);
    });

    const onNew = (payload) => {
      const { threadId, message } = payload || {};
      if (!threadId) return;
      if (message?.autor_id && toInt(message.autor_id) === toInt(userId))
        return;
      if (selectedRef.current?.id === threadId) return;

      setThreads((prev) => {
        const next = prev.map((t) =>
          t.id === threadId
            ? {
                ...t,
                unread_count: (Number(t.unread_count) || 0) + 1,
                last_msg_id: Math.max(
                  t.last_msg_id || 0,
                  toInt(payload.message?.id) || t.last_msg_id || 0
                ),
              }
            : t
        );
        return next;
      });
    };

    const onInThread = async (payload) => {
      const { threadId, message } = payload || {};
      if (!threadId || !message) return;

      const msg = normalizeMsg(message);

      if (selectedRef.current && selectedRef.current.id === threadId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          const merged = [...prev, msg].sort((a, b) => a.id - b.id);
          lastMsgIdRef.current = maxRealMsgId(merged);
          return merged;
        });
        try {
          await MsgApi.setReadPointer(
            { threadId, last_read_message_id: msg.id },
            userId
          );
          setThreads((prev) =>
            prev.map((t) =>
              t.id === threadId
                ? {
                    ...t,
                    last_read_message_id: Math.max(
                      t.last_read_message_id || 0,
                      msg.id
                    ),
                    unread_count: 0,
                  }
                : t
            )
          );
        } catch {}
        if (isNearBottom()) await scrollToEnd();
      } else {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === threadId
              ? {
                  ...t,
                  last_msg_id: Math.max(t.last_msg_id || 0, msg.id),
                }
              : t
          )
        );
      }
    };

    socket.on("msg:new", onNew);
    socket.on("msg:new:in-thread", onInThread);

    return () => {
      socket.off("msg:new", onNew);
      socket.off("msg:new:in-thread", onInThread);
      if (selectedRef.current?.id)
        socket.emit("thread:leave", selectedRef.current.id);
    };
  }, [socket, userId, setThreads, isNearBottom, scrollToEnd]);

  // enviar
  const sendMessage = useCallback(async () => {
    if (!selectedRef.current)
      return showError("Selecione uma conversa.");
    const text = String(composer || "").trim();
    if (!text) return;
    setSending(true);
    const optimisticId = -Date.now();
    try {
      const optimisticMsg = normalizeMsg({
        id: optimisticId,
        thread_id: selectedRef.current.id,
        autor_id: userId,
        body: text,
        created_at: new Date().toISOString(),
      });
      setMessages((prev) => [...prev, optimisticMsg]);
      setComposer("");
      await sleep(10);
      const { id } = await MsgApi.postMessage(
        { threadId: selectedRef.current.id, body: text },
        userId
      );

      // substitui otimista pelo real via reload incremental
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      await loadMessages(
        selectedRef.current.id,
        isRealId(toInt(lastMsgIdRef.current))
          ? toInt(lastMsgIdRef.current)
          : 0
      );

      await MsgApi.setReadPointer(
        {
          threadId: selectedRef.current.id,
          last_read_message_id: toInt(id),
        },
        userId
      );
      loadThreads();
      if (isNearBottom()) await scrollToEnd();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      showError(
        e?.response?.data?.message ||
          e.message ||
          "Falha ao enviar."
      );
    } finally {
      setSending(false);
    }
  }, [composer, loadMessages, loadThreads, isNearBottom, scrollToEnd, userId]);

  const onComposerKey = (ev) => {
    if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault();
      sendMessage();
    }
  };

  // criar conversa
  const createThread = useCallback(async () => {
    const payload = { tipo: formCreate.tipo };
    if (formCreate.tipo === "GRUPO") {
      if (!formCreate.titulo?.trim())
        return showError("Título do grupo é obrigatório.");
      payload.titulo = formCreate.titulo.trim();
    }
    if (formCreate.tipo !== "AUTO") {
      if (
        formCreate.tipo === "DM" &&
        formCreate.participantes.length !== 1
      )
        return showError("DM exige exatamente 1 participante.");
      payload.participantes = formCreate.participantes;
    }
    setCreating(true);
    try {
      const { id } = await MsgApi.createThread(payload, userId);
      showInfo(`Conversa #${id} criada.`);
      setCreateOpen(false);
      setFormCreate({ tipo: "DM", titulo: "", participantes: [] });
      await loadThreads();
      const fresh = await MsgApi.listThreads({ limit: 50 }, userId);
      const pick = (Array.isArray(fresh) ? fresh : []).find(
        (t) => toInt(t.id) === toInt(id)
      );
      if (pick)
        await selectThread({
          ...pick,
          id: toInt(pick.id),
          last_msg_id: toInt(pick.last_msg_id) || 0,
          last_read_message_id: toInt(pick.last_read_message_id) || 0,
          participantes: normalizeParticipants(pick),
        });
    } catch (e) {
      showError(
        e?.response?.data?.message ||
          e.message ||
          "Falha ao criar."
      );
    } finally {
      setCreating(false);
    }
  }, [formCreate, loadThreads, selectThread, userId]);

  // derivado: threads + contagem de não lidas
  const threadsView = useMemo(
    () =>
      threads.map((t) => {
        const unread =
          typeof t.unread_count === "number"
            ? t.unread_count
            : Math.max(
                0,
                (toInt(t.last_msg_id) || 0) -
                  (toInt(t.last_read_message_id) || 0)
              );
        return {
          ...t,
          unreadCount: unread,
          unread: unread > 0,
        };
      }),
    [threads]
  );

  // ponto único de verdade para badge global de mensagens
  useEffect(() => {
    const totalUnread = threadsView.reduce(
      (sum, t) => sum + (t.unread ? t.unreadCount : 0),
      0
    );
    if (totalUnread > 0) setBadge("mensagens", totalUnread);
    else clearBadge("mensagens");
  }, [threadsView, setBadge, clearBadge]);

  // reabrir última conversa
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    const lastId = toInt(localStorage.getItem(STORAGE_LAST_THREAD));
    if (!lastId) return;
    const t = threads.find((x) => toInt(x.id) === lastId);
    if (t) {
      didAutoSelectRef.current = true;
      selectThread(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, selectThread]);

  // troca de seleção: garantir leave (WS)
  useEffect(() => {
    return () => {
      if (socket && selectedRef.current?.id) {
        socket.emit("thread:leave", selectedRef.current.id);
      }
    };
  }, [socket]);

  /* =============== JSX =============== */
  return (
    <MainLayout>
      <div className="ssw-container ssw-container--fluid">
        <div className="card" style={{ marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h1 style={{ margin: 0 }}>Comunicação Interna</h1>
              <div className="muted">
                Usuário ativo: <b>{getName(userId)}</b>
              </div>
            </div>
            <div>
              <Button onClick={() => setCreateOpen(true)}>
                Nova conversa
              </Button>
            </div>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            <b>Como funciona:</b> cada <i>conversa</i> é um canal de
            mensagens.
            <b> DM</b> (1:1), <b>GRUPO</b> (várias pessoas) ou{" "}
            <b>AUTO</b> (avisos automáticos).
          </div>
        </div>

        <div className="mi">
          {/* Sidebar */}
          <aside className="mi__sidebar">
            <div className="mi__bar">
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <Label>Filtrar</Label>
                <div className="segmented">
                  {["ALL", "DM", "GRUPO", "AUTO"].map((k) => (
                    <button
                      key={k}
                      className={filterTipo === k ? "active" : ""}
                      onClick={() => setFilterTipo(k)}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mi__search">
              <Input
                placeholder="Buscar conversas…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="mi__threads">
              {threadsLoading && <p className="muted">Carregando…</p>}
              {!threadsLoading && threadsView.length === 0 && (
                <div className="muted">Nenhuma conversa encontrada.</div>
              )}
              <ul className="list">
                {threadsView.map((t) => (
                  <li key={t.id}>
                    <button
                      className={`thread ${
                        selected?.id === t.id ? "thread--active" : ""
                      }`}
                      onClick={() => selectThread(t)}
                    >
                      <div className="thread__title">
                        <span className="thread__tipo">{t.tipo}</span>
                        <span className="thread__nome">
                          {t.resolved_title?.trim()
                            ? t.resolved_title
                            : t.titulo?.trim()
                            ? t.titulo
                            : (t.participantes || [])
                                .filter((id) => id !== userId)
                                .map((id) => getName(id))
                                .join(", ") ||
                              (t.tipo === "AUTO"
                                ? "Automático"
                                : `Conversa #${t.id}`)}
                        </span>
                      </div>
                      <div className="thread__meta">
                        <span className="muted">#{t.id}</span>
                        {t.unread ? (
                          <span className="badge">
                            {t.unreadCount > 99 ? "99+" : t.unreadCount}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Painel */}
          <section className="mi__panel">
            <div className="mi__header">
              <h2>Mensagens</h2>
              {selected ? (
                <div className="mi__meta">
                  <span className="chip">#{selected.id}</span>
                  <span className="chip">{selected.tipo}</span>
                  {selected.titulo ? (
                    <span className="chip chip--ok">{selected.titulo}</span>
                  ) : null}
                  <div className="spacer" />
                  <Button
                    variant="ghost"
                    onClick={() =>
                      selected &&
                      loadMessages(
                        selected.id,
                        isRealId(toInt(lastMsgIdRef.current))
                          ? toInt(lastMsgIdRef.current)
                          : 0
                      )
                    }
                  >
                    Puxar novas
                  </Button>
                  <Button variant="ghost" onClick={loadThreads}>
                    Sync
                  </Button>
                </div>
              ) : (
                <span className="muted">Selecione uma conversa</span>
              )}
            </div>

            <div className="mi__messages" ref={panelRef}>
              {selected == null ? (
                <div className="muted pad">Nenhuma conversa selecionada.</div>
              ) : msgLoading && messages.length === 0 ? (
                <div className="muted pad">Carregando mensagens…</div>
              ) : messages.length === 0 ? (
                <div className="muted pad">Sem mensagens.</div>
              ) : (
                <ul className="list">
                  {messages.map((m) => (
                    <li
                      key={m.id}
                      className={`msg ${
                        toInt(m.autor_id) === toInt(userId) ? "msg--self" : ""
                      }`}
                    >
                      <div className="msg__bubble">
                        <div className="msg__head">
                          <span className="msg__author">
                            {getName(m.autor_id)}
                          </span>
                          <span className="msg__time">
                            {(() => {
                              const d = m.created_at
                                ? new Date(m.created_at)
                                : null;
                              return d && !isNaN(d.valueOf())
                                ? d.toLocaleString("pt-BR", {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                  })
                                : "-";
                            })()}
                          </span>
                        </div>
                        <div className="msg__body">{m.body}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div ref={msgEndRef} />
            </div>

            <div className="mi__composer">
              <textarea
                className="textarea input"
                placeholder={
                  selected
                    ? "Digite… (Ctrl+Enter para enviar)"
                    : "Selecione uma conversa"
                }
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={onComposerKey}
                disabled={!selected || sending}
                rows={3}
              />
              <Button
                onClick={sendMessage}
                disabled={!selected || sending || !composer.trim()}
              >
                {sending ? "Enviando…" : "Enviar"}
              </Button>
            </div>
          </section>
        </div>

        {/* Drawer: Nova conversa */}
        <PanelDrawer
          open={createOpen}
          title="Nova conversa"
          onClose={() => setCreateOpen(false)}
        >
          {({ firstFieldRef }) => (
            <>
              <div className="grid grid-2">
                <div className="col-span-2">
                  <Label>Tipo</Label>
                  <div className="segmented">
                    {["DM", "GRUPO", "AUTO"].map((k) => (
                      <button
                        key={k}
                        className={formCreate.tipo === k ? "active" : ""}
                        onClick={() =>
                          setFormCreate((s) => ({
                            ...s,
                            tipo: k,
                            participantes:
                              k === "AUTO"
                                ? []
                                : k === "DM"
                                ? s.participantes.slice(0, 1)
                                : s.participantes,
                          }))
                        }
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>

                {formCreate.tipo === "GRUPO" && (
                  <div className="col-span-2">
                    <Label>Título do grupo</Label>
                    <Input
                      ref={firstFieldRef}
                      placeholder="Ex.: Time Dev"
                      value={formCreate.titulo}
                      onChange={(e) =>
                        setFormCreate((s) => ({
                          ...s,
                          titulo: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}

                {formCreate.tipo !== "AUTO" && (
                  <div className="col-span-2">
                    <Label>
                      {formCreate.tipo === "DM"
                        ? "Participante (1 pessoa)"
                        : "Participantes"}
                    </Label>

                    {/* Chips */}
                    <div
                      className="chips sel"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 6,
                      }}
                    >
                      {formCreate.participantes.length === 0 && (
                        <span className="muted">Nenhum selecionado</span>
                      )}
                      {formCreate.participantes.map((pid) => (
                        <span key={pid} className="chip">
                          {getName(pid)}{" "}
                          <span className="muted">#{pid}</span>
                          <button
                            className="chip-close"
                            onClick={() =>
                              removeParticipante(pid, setFormCreate)
                            }
                            aria-label="Remover"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>

                    {/* SelectFuncionario (adiciona 1 por vez) */}
                    <SelectFuncionario
                      placeholder="Busque por nome, usuário, CPF ou ID"
                      onChange={(func) =>
                        func &&
                        addParticipante(
                          func,
                          userId,
                          setFormCreate,
                          mergeNames
                        )
                      }
                      defaultOptions={false}
                      minChars={2}
                      includeBloqueados={false}
                      disabled={
                        formCreate.tipo === "DM" &&
                        formCreate.participantes.length >= 1
                      }
                    />

                    <div className="hint" style={{ marginTop: 6 }}>
                      Dica: selecione e ele será incluído nos chips acima. Em
                      DM, limite é 1.
                    </div>
                  </div>
                )}
              </div>

              <div
                className="flex justify-end mt-3"
                style={{ gap: 8 }}
              >
                <Button
                  variant="secondary"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={createThread} disabled={creating}>
                  {creating ? "Criando…" : "Criar"}
                </Button>
              </div>
            </>
          )}
        </PanelDrawer>
      </div>
    </MainLayout>
  );
}
